import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { formatCurrency, getRegimeName } from '../lib/payrollEngine';

export function LegalPage() {
  const { company } = useAuth();
  const uit        = company?.uit  ?? 5500;
  const rmv        = company?.rmv  ?? 1025;
  const currentYear = new Date().getFullYear();

  const contributions = [
    { name: 'EsSalud', employer: `${company?.essalud_rate || 9}%`, employee: '—', note: 'Seguro de salud. Aporte del empleador sobre remuneración.' },
    { name: 'ONP', employer: '—', employee: '13%', note: 'Pensión. Descuento al trabajador. Alternativa: AFP.' },
    { name: 'AFP', employer: '—', employee: '~10-12%', note: 'Aporte obligatorio + comisión + seguro de invalidez.' },
    { name: 'SCTR', employer: 'Variable', employee: '—', note: 'Seguro Complementario de Trabajo de Riesgo (actividades peligrosas).' },
    { name: 'Renta 5ta Cat.', employer: '—', employee: 'Progresivo', note: `8% a 30% sobre renta anual neta > 7 UIT (S/ ${(7 * uit).toLocaleString('es-PE')}). Se retiene mensualmente.` },
  ];

  const deadlines = [
    { month: 'Mensual', day: '10-15', concept: 'PLAME y PDT 601', desc: 'Declaración mensual de planilla electrónica en SUNAT' },
    { month: 'Mensual', day: '15', concept: 'ONP', desc: 'Pago de aportes al Sistema Nacional de Pensiones (ONP)' },
    { month: 'Mensual', day: '10-15', concept: 'AFP', desc: 'Transferencia de aportes AFP mediante SUNAT' },
    { month: 'Mensual', day: '15', concept: 'EsSalud', desc: 'Pago del aporte del empleador a EsSalud' },
    { month: 'Mayo/Nov', day: '15', concept: 'CTS', desc: 'Depósito semestral de CTS en cuenta del trabajador (Mayo y Noviembre)' },
    { month: 'Jul/Dic', day: '15', concept: 'Gratificaciones', desc: 'Pago de gratificaciones legales antes del 15 de julio y diciembre' },
    { month: 'Anual', day: 'Mar/Abr', concept: 'Renta 5ta Reg.', desc: 'Regularización anual impuesto a la renta 5ta categoría' },
  ];

  const regimeRules: Record<string, any[]> = {
    general: [
      { benefit: 'Vacaciones', rule: '30 días calendario por año de servicio' },
      { benefit: 'Gratificaciones', rule: '1 remuneración completa en julio y diciembre' },
      { benefit: 'CTS', rule: '1/12 de remuneración mensual + 1/6 de gratificación, depositada en mayo y noviembre' },
      { benefit: 'Seguro EsSalud', rule: '9% de la remuneración bruta, a cargo del empleador' },
      { benefit: 'Asignación Familiar', rule: '10% de la RMV para trabajadores con hijos menores de 18 años' },
      { benefit: 'Utilidades', rule: '10% de la renta neta para empresas con más de 20 trabajadores' },
      { benefit: 'Descanso semanal', rule: '1 día de descanso semanal, generalmente domingo' },
    ],
    pequeña_empresa: [
      { benefit: 'Vacaciones', rule: '15 días calendario por año de servicio (50% del régimen general)' },
      { benefit: 'Gratificaciones', rule: '½ remuneración en julio y ½ remuneración en diciembre' },
      { benefit: 'CTS', rule: '50% del beneficio del régimen general' },
      { benefit: 'Seguro EsSalud', rule: '9% (igual que régimen general)' },
      { benefit: 'Asignación Familiar', rule: '10% de la RMV (igual que régimen general)' },
      { benefit: 'Utilidades', rule: 'Aplica cuando corresponde según actividad' },
    ],
    microempresa: [
      { benefit: 'Vacaciones', rule: 'No obligatorio (puede otorgarse por acuerdo)' },
      { benefit: 'Gratificaciones', rule: 'No aplica' },
      { benefit: 'CTS', rule: 'No aplica' },
      { benefit: 'Seguro Salud', rule: 'SIS subsidiado (Seguro Integral de Salud)' },
      { benefit: 'Asignación Familiar', rule: 'No aplica' },
      { benefit: 'Utilidades', rule: 'No aplica' },
    ],
  };

  const currentRules = regimeRules[company?.regime || 'general'] || regimeRules.general;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-on-surface font-headline">Legal y Cumplimiento</h1>
          <p className="text-on-surface-variant mt-1">Marco normativo laboral peruano aplicable a tu empresa</p>
        </div>
        <span className="px-4 py-2 bg-tertiary-fixed text-on-tertiary-fixed rounded-full text-xs font-bold uppercase tracking-wider">
          {getRegimeName(company?.regime || 'general')}
        </span>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Main: contributions + regime rules */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          {/* Aportes y retenciones */}
          <div className="card p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold font-headline text-on-surface">Tasas de Aportes y Retenciones</h2>
              <span className="text-xs font-bold px-3 py-1 bg-tertiary-fixed text-on-tertiary-fixed rounded-full">Año Fiscal {currentYear}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-surface-container-low p-5 rounded-xl border-l-4 border-primary">
                <p className="text-xs text-on-surface-variant font-medium uppercase tracking-wider mb-2">EsSalud (Empleador)</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-headline font-extrabold text-primary">{company?.essalud_rate || 9}%</span>
                  <span className="text-[10px] text-on-surface-variant font-bold uppercase">Empleador</span>
                </div>
                <p className="text-xs text-on-surface-variant mt-1">Sobre remuneración mensual bruta</p>
              </div>
              <div className="bg-surface-container-low p-5 rounded-xl border-l-4 border-secondary">
                <p className="text-xs text-on-surface-variant font-medium uppercase tracking-wider mb-2">ONP / AFP</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-headline font-extrabold text-secondary">{company?.snp_rate || 13}%</span>
                  <span className="text-[10px] text-on-surface-variant font-bold uppercase">Trabajador</span>
                </div>
                <p className="text-xs text-on-surface-variant mt-1">{company?.afp_system === 'afp' ? 'AFP (aporte + comisión + seguro)' : 'ONP - Sistema Nacional de Pensiones'}</p>
              </div>
            </div>
            <div className="overflow-hidden rounded-xl border border-outline-variant/10">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-surface-container-low">
                    {['Concepto', 'Empleador', 'Trabajador', 'Descripción'].map(h => (
                      <th key={h} className="px-4 py-3 text-[10px] font-bold text-on-surface-variant uppercase tracking-widest">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {contributions.map(c => (
                    <tr key={c.name} className="hover:bg-surface-container-low/30 transition-colors">
                      <td className="px-4 py-3 font-bold text-on-surface">{c.name}</td>
                      <td className="px-4 py-3 font-semibold text-primary">{c.employer}</td>
                      <td className="px-4 py-3 font-semibold text-error">{c.employee}</td>
                      <td className="px-4 py-3 text-xs text-on-surface-variant">{c.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Regime rules */}
          <div className="card p-8">
            <h2 className="text-lg font-bold font-headline mb-6">Beneficios según {getRegimeName(company?.regime || 'general')}</h2>
            <div className="space-y-3">
              {currentRules.map((r, i) => (
                <div key={i} className="flex gap-4 p-4 bg-surface-container-low rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="material-symbols-outlined text-primary text-[16px]">check_circle</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-on-surface">{r.benefit}</p>
                    <p className="text-xs text-on-surface-variant mt-0.5">{r.rule}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
              <strong>Base legal:</strong> Ley de Productividad y Competitividad Laboral (D.Leg. 728), Ley MYPE (D.Leg. 1086), Ley de Gratificaciones (Ley 27735), Ley de CTS (D.Leg. 650).
            </div>
          </div>
        </div>

        {/* Sidebar: deadlines */}
        <div className="col-span-12 lg:col-span-4 space-y-6">
          <div className="card p-6">
            <h2 className="text-lg font-bold font-headline mb-5">Calendario de Obligaciones</h2>
            <div className="space-y-3">
              {deadlines.map((d, i) => (
                <div key={i} className="flex gap-3 p-3 bg-surface-container-low/50 rounded-xl">
                  <div className="flex flex-col items-center justify-center bg-white min-w-[48px] h-[48px] rounded-lg shadow-sm border-t-4 border-primary flex-shrink-0">
                    <span className="text-[9px] font-bold uppercase text-on-surface-variant">{d.month}</span>
                    <span className="text-base font-headline font-black text-on-surface leading-tight">{d.day}</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-on-surface">{d.concept}</h4>
                    <p className="text-xs text-on-surface-variant">{d.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Parámetros legales vigentes — valores de company */}
          <div className="bg-primary rounded-xl p-6 text-white">
            <h4 className="text-xs font-bold uppercase tracking-widest opacity-70 mb-1">Parámetros legales vigentes</h4>
            <p className="text-[10px] opacity-50 mb-4">
              Año {currentYear} · Editables en Configuración
            </p>
            <div className="space-y-2.5 text-sm">
              {[
                { label: 'UIT',               value: formatCurrency(uit),           note: 'Unidad Impositiva Tributaria' },
                { label: 'RMV',               value: formatCurrency(rmv),           note: 'Remuneración Mínima Vital' },
                { label: 'Asig. Familiar',    value: formatCurrency(rmv * 0.10),    note: '10% de la RMV' },
                { label: '7 UIT (5ta cat.)',  value: formatCurrency(7 * uit),       note: 'Deducción anual base' },
                { label: 'EsSalud',           value: `${company?.essalud_rate ?? 9}%`, note: 'Aporte empleador' },
                { label: `ONP${company?.afp_system === 'afp' ? '/AFP' : ''}`, value: `${company?.snp_rate ?? 13}%`, note: 'Aporte trabajador' },
              ].map(r => (
                <div key={r.label}>
                  <div className="flex justify-between">
                    <span className="opacity-80 font-medium">{r.label}</span>
                    <span className="font-bold">{r.value}</span>
                  </div>
                  <p className="text-[10px] opacity-50">{r.note}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Tramos 5ta categoría */}
          <div className="card p-5">
            <h3 className="font-bold text-on-surface font-headline text-sm mb-4">
              Tramos Renta 5ta Categoría {currentYear}
            </h3>
            <div className="space-y-2">
              {[
                { range: `Hasta 5 UIT (${formatCurrency(5*uit)})`,       rate: '8%'  },
                { range: `5 – 20 UIT (${formatCurrency(20*uit)})`,        rate: '14%' },
                { range: `20 – 35 UIT (${formatCurrency(35*uit)})`,       rate: '17%' },
                { range: `35 – 45 UIT (${formatCurrency(45*uit)})`,       rate: '20%' },
                { range: `Más de 45 UIT`,                                  rate: '30%' },
              ].map(t => (
                <div key={t.rate} className="flex items-center justify-between p-2.5 bg-surface-container-low rounded-lg text-xs">
                  <span className="text-on-surface-variant">{t.range}</span>
                  <span className="font-black text-primary">{t.rate}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-on-surface-variant mt-3">
              Base = renta bruta anual − 7 UIT ({formatCurrency(7*uit)})
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
